import React from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useLogout, useGetIdentity } from 'react-admin';
import { buildLabel, buildDetails } from '../../version';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '../ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Users,
  MapPin,
  Star,
  MessageSquare,
  List,
  Smartphone,
  Bell,
  LayoutDashboard,
  Settings,
  LogOut,
  User2,
  MessageCircle,
  UserCheck,
  Award,
  Calendar,
  BookOpen,
  AppWindow,
  Activity,
  Trophy as TrophyIcon,
  ChevronUp,
  CreditCard,
  Banknote,
  RotateCcw,
  Webhook,
  ShieldCheck,
} from 'lucide-react';

type NavItem = {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Optional pending-count badge. Wired to live data in later phases. */
  count?: number;
};

// Navigation data with groups but no collapsible behavior
const navigationData: { main: NavItem[]; groups: { label: string; items: NavItem[] }[] } = {
  main: [
    {
      title: "Dashboard",
      url: "/",
      icon: LayoutDashboard,
    },
  ],
  groups: [
    {
      label: "Core Resources",
      items: [
        { title: "Users", url: "/users", icon: Users },
        { title: "Trainer Applications", url: "/trainer-applications", icon: UserCheck },
        { title: "Specialty Certificates", url: "/specialty-certificates", icon: ShieldCheck },
        { title: "Trainers", url: "/trainers", icon: Award },
        { title: "Locations", url: "/locations", icon: MapPin },
        { title: "Sports", url: "/sports", icon: TrophyIcon },
      ],
    },
    {
      label: "Sessions & Bookings",
      items: [
        { title: "Sessions", url: "/sessions", icon: Calendar },
        { title: "Bookings", url: "/bookings", icon: BookOpen },
      ],
    },
    {
      label: "Payments",
      items: [
        { title: "Payments", url: "/payments", icon: CreditCard },
        { title: "Payouts", url: "/payouts", icon: Banknote },
        { title: "Refunds", url: "/refunds", icon: RotateCcw },
        { title: "Payment Webhooks", url: "/payment-webhooks", icon: Webhook },
      ],
    },
    {
      label: "Reviews",
      items: [
        { title: "Session Reviews", url: "/session-reviews", icon: Star },
      ],
    },
    {
      label: "Chat Management",
      items: [
        { title: "Chat Monitoring", url: "/chat-monitoring", icon: Activity },
        { title: "Session Chats", url: "/session-chats", icon: MessageCircle },
        { title: "Direct Chats", url: "/direct-chats", icon: MessageSquare },
      ],
    },
    {
      label: "Notifications",
      items: [
        { title: "Notifications", url: "/notifications", icon: Bell },
        { title: "Notification Logs", url: "/notification-logs", icon: List },
        { title: "Device Tokens", url: "/device-tokens", icon: Smartphone },
      ],
    },
  ],
};

/**
 * Count badge: a pill when expanded, a small orange dot when collapsed.
 * DESIGN_SYSTEM.md §5.
 */
function NavCount({ count, collapsed }: { count?: number; collapsed: boolean }) {
  if (!count) return null;
  if (collapsed) {
    return (
      <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-orange" aria-hidden />
    );
  }
  return (
    <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-pill bg-indigo-tint px-1.5 text-[11px] font-semibold text-indigo">
      {count}
    </span>
  );
}

function NavLinkItem({ item, isActive, collapsed }: { item: NavItem; isActive: boolean; collapsed: boolean }) {
  return (
    <SidebarMenuItem className="relative">
      <SidebarMenuButton tooltip={item.title} asChild isActive={isActive}>
        <Link to={item.url}>
          <item.icon className="size-4 shrink-0" />
          <span className="flex-1">{item.title}</span>
          {!collapsed && <NavCount count={item.count} collapsed={false} />}
        </Link>
      </SidebarMenuButton>
      {collapsed && <NavCount count={item.count} collapsed />}
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const logout = useLogout();
  const { data: identity } = useGetIdentity();
  const { state, isMobile, openMobile, setOpenMobile } = useSidebar();
  const collapsed = state === 'closed';

  // On mobile the sidebar is an off-canvas drawer; close it once the user
  // navigates so it doesn't stay open over the newly selected screen.
  React.useEffect(() => {
    if (isMobile && openMobile) {
      setOpenMobile(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader>
        <div className="flex items-center justify-between gap-1 px-1 py-1 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-0">
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <img
              src="/assets/latio-logo.png"
              alt="Latio"
              className="size-8 shrink-0 rounded-latio-sm"
            />
            <span className="flex items-baseline gap-1.5 group-data-[collapsible=icon]:hidden">
              <span
                className="text-[19px] font-bold leading-none tracking-tight text-ink"
                style={{ letterSpacing: '-0.02em' }}
              >
                Lat<span className="text-indigo">i</span>o
              </span>
              <span className="text-xs font-semibold text-ink-2">Admin</span>
            </span>
          </Link>
          <SidebarTrigger className="size-7 shrink-0 text-ink-2" />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Main Dashboard */}
        <SidebarGroup>
          <SidebarGroupLabel>Platform</SidebarGroupLabel>
          <SidebarMenu className="group-data-[collapsible=icon]:items-center">
            {navigationData.main.map((item) => (
              <NavLinkItem
                key={item.title}
                item={item}
                isActive={location.pathname === item.url}
                collapsed={collapsed}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>

        {/* Grouped Navigation - Always expanded */}
        {navigationData.groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu className="group-data-[collapsible=icon]:items-center">
              {group.items.map((item) => {
                const isActive =
                  item.url === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.url);
                return (
                  <NavLinkItem
                    key={item.title}
                    item={item}
                    isActive={isActive}
                    collapsed={collapsed}
                  />
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <SidebarMenuItem>
            {/* User entry lives ONLY here (DESIGN_SYSTEM §5) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                >
                  <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-latio-sm bg-surface-2">
                    {identity?.avatar ? (
                      <img src={identity.avatar} alt={identity.fullName || 'User'} className="size-full object-cover" />
                    ) : (
                      <User2 className="size-4" />
                    )}
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {identity?.fullName || 'Admin'}
                    </span>
                    <span className="truncate text-xs text-ink-2">
                      {identity?.email || 'Administrator'}
                    </span>
                  </div>
                  <ChevronUp className="ml-auto size-4" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-latio-sm"
                side="top"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <div className="flex aspect-square size-8 items-center justify-center rounded-latio-sm bg-surface-2">
                      <User2 className="size-4" />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{identity?.fullName || 'Admin'}</span>
                      <span className="truncate text-xs text-ink-2">{identity?.email || 'Administrator'}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/account" className="cursor-pointer">
                    <User2 className="mr-2 h-4 w-4" />
                    Account
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="px-2 py-1 text-xs font-normal text-ink-2">System</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link to="/app-versions" className="cursor-pointer">
                    <AppWindow className="mr-2 h-4 w-4" />
                    App Versions
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/system-health" className="cursor-pointer">
                    <Activity className="mr-2 h-4 w-4" />
                    System Health
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/settings" className="cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer text-error focus:text-error"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* Hidden when the sidebar collapses to icons — there is no room, and
            the full detail lives on System Health. */}
        <p
          className="px-2 pb-1 text-[11px] leading-none text-sidebar-foreground/50 group-data-[collapsible=icon]:hidden"
          title={buildDetails()
            .map(({ label, value }) => `${label}: ${value}`)
            .join('\n')}
        >
          {buildLabel}
        </p>
      </SidebarFooter>
    </Sidebar>
  );
}
